import { getAttrKey } from 'src/packages/utils/get-attr-key';
import { hasValue } from 'src/packages/utils/has-value';

import type { DirectoryRecord } from './entities/directory-record.entity';
import type { TControlView, TEmbeddedArrayValue, TSerializedRecord } from './types';

function checkIsValidControlValue(value: unknown): boolean {
  if (value === null || (Array.isArray(value) && value.length === 0)) {
    return true;
  }

  if (typeof value === 'number') {
    return !Number.isNaN(value);
  }

  return hasValue(value);
}

function isEmbeddedArray(value: unknown): value is TEmbeddedArrayValue[] {
  return !!value && Array.isArray(value) && value.every((v) => typeof v === 'object');
}

export function serializeDirectoryRecordToSave(
  record: DirectoryRecord,
  controlViews: TControlView[],
  additionalRecordValues?: Record<string, unknown>,
): TSerializedRecord {
  const serializedRecord: TSerializedRecord = { ...(additionalRecordValues ?? {}) };

  for (const fieldView of controlViews) {
    if (!fieldView.attrName) {
      continue;
    }

    const value = record.data[fieldView.fieldId];

    if (value === null || checkIsValidControlValue(value)) {
      if (fieldView.isEmbeddedArray) {
        // If the control is part of the embedded array attrName is a string like "Common_WellPassing.data.passingByWellPurpose.10048.passing".
        // This string corresponds to the schema:
        // 0 - directory name,
        // 1 - "data", static string,
        // 2 - record key,
        // 3 - embedded field id,
        // 4 - embedded field name,
        const [directoryName, _, key, embeddedFieldId, fieldName] = fieldView.attrName.split('.');
        const [embeddedItemFieldIdName, embeddedItemFieldIdValue] = fieldView.fieldId.split('.');

        if (key) {
          const existingArray = serializedRecord[key];

          const embeddedArrayRecord: TEmbeddedArrayValue = {
            [embeddedItemFieldIdName]: embeddedItemFieldIdValue,
            [fieldName]: value,
          };

          if (isEmbeddedArray(existingArray)) {
            existingArray.push(embeddedArrayRecord);
          } else {
            serializedRecord[key] = [embeddedArrayRecord];
          }
        }
      } else {
        const key = fieldView.attrName.split('.').at(-1);

        if (key) {
          serializedRecord[key] = value;
        }
      }
    }
  }

  return serializedRecord;
}

/** Parses serialized record data from database/localstorage and returns a map like
 * { control_attr_name: control_value } */
export function deserializeDirectoryRecord(
  recordData: Record<string, unknown>,
  controlViews: TControlView[],
): Record<string, unknown> {
  const controlValues: Record<string, unknown> = {};

  for (const controlView of controlViews) {
    if (!controlView.attrName) {
      continue;
    }

    if (controlView.isEmbeddedArray) {
      const [directoryName, dataStaticString, key, fieldId, fieldName] = controlView.attrName.split('.');
      const [fieldIdKey, fieldIdValue] = controlView.fieldId.split('.');
      const value = recordData[key];

      if (isEmbeddedArray(value)) {
        const controlValue = value.find((object) => object[fieldIdKey]?.toString() === fieldIdValue.toString());

        if (controlValue) {
          controlValues[controlView.fieldId] = controlValue[fieldName];
        }
      }
    } else {
      const attrKey = getAttrKey(controlView.attrName);
      const value = recordData[attrKey];

      if (hasValue(value)) {
        controlValues[controlView.fieldId] = value;
      } else {
        controlValues[controlView.fieldId] = null;
      }
    }
  }

  return controlValues;
}
